import { useState } from 'react';
import { api } from '../api';

const fmt = v => '₹' + Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 });

export default function ProductUnsoldList({ products, start, end }) {
  const [expanded,  setExpanded]  = useState(null);
  const [data,      setData]      = useState({});
  const [loading,   setLoading]   = useState(null);
  const [search,    setSearch]    = useState('');
  const [view,      setView]      = useState('unsold'); // 'unsold' | 'sold'

  const filtered = products.filter(p =>
    p.product_name.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = async (productName) => {
    if (expanded === productName) { setExpanded(null); return; }
    setExpanded(productName);
    if (!data[productName]) {
      setLoading(productName);
      try {
        const res = await api.productUnsold(start, end, productName);
        setData(prev => ({ ...prev, [productName]: res }));
      } catch {
        setData(prev => ({ ...prev, [productName]: null }));
      } finally {
        setLoading(null);
      }
    }
  };

  return (
    <div className="card">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
        <div className="card-title" style={{ margin: 0 }}>
          All products — state coverage
          <span style={{ marginLeft: '8px', fontSize: '12px', color: 'var(--muted)', fontWeight: 400 }}>
            ({filtered.length} products)
          </span>
        </div>

        {/* Search */}
        <input
          type="text"
          placeholder="Search product..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            padding: '7px 12px', border: '1px solid var(--border)',
            borderRadius: '8px', fontSize: '13px', width: '220px',
            outline: 'none', fontFamily: 'inherit',
          }}
        />
      </div>

      {/* View toggle (sold / unsold) */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '14px' }}>
        {[
          { key: 'unsold', label: '❌ Not sold in', color: 'var(--red)' },
          { key: 'sold',   label: '✅ Sold in',     color: 'var(--green)' },
        ].map(v => (
          <button key={v.key} onClick={() => setView(v.key)} style={{
            padding: '5px 14px', borderRadius: '8px', fontSize: '12px',
            fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
            background: view === v.key ? v.color : 'var(--surface-2)',
            color:      view === v.key ? '#fff'   : 'var(--muted)',
            border: `1px solid ${view === v.key ? v.color : 'var(--border)'}`,
            transition: 'all .15s',
          }}>
            {v.label}
          </button>
        ))}
      </div>

      {/* Product list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {filtered.map((p, i) => {
          const isOpen    = expanded === p.product_name;
          const isLoading = loading  === p.product_name;
          const info      = data[p.product_name];
          const showList  = info ? (view === 'unsold' ? info.unsold_states : info.sold_states) : [];

          return (
            <div key={p.product_name}>

              {/* ── Product row ──────────────────── */}
              <div
                onClick={() => toggle(p.product_name)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '28px 1fr auto auto auto',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  background: isOpen ? 'var(--surface-hover)' : 'transparent',
                  border: `1px solid ${isOpen ? 'var(--accent-border)' : 'transparent'}`,
                  transition: 'all .15s',
                }}
              >
                {/* Rank */}
                <div style={{ fontSize: '11px', color: 'var(--light)', textAlign: 'center' }}>
                  {i + 1}
                </div>

                {/* Name + arrow */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                  <span style={{
                    fontSize: '11px',
                    color: isOpen ? 'var(--green)' : 'var(--muted)',
                    transition: 'transform .2s',
                    display: 'inline-block',
                    transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                    flexShrink: 0,
                  }}>▶</span>
                  <span style={{
                    fontWeight: 500, fontSize: '13px',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {p.product_name}
                  </span>
                </div>

                {/* Revenue */}
                <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--green)', whiteSpace: 'nowrap' }}>
                  {fmt(p.revenue)}
                </div>

                {/* Sold / unsold badges — show if data already loaded */}
                {info ? (
                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: '999px', fontSize: '11px',
                      fontWeight: 600, background: 'var(--green-light)', color: 'var(--green-dark)',
                      whiteSpace: 'nowrap',
                    }}>
                      ✅ {info.sold_in} states
                    </span>
                    <span style={{
                      padding: '2px 8px', borderRadius: '999px', fontSize: '11px',
                      fontWeight: 600, background: 'var(--red-light)', color: 'var(--red-dark)',
                      whiteSpace: 'nowrap',
                    }}>
                      ❌ {info.not_sold_in} states
                    </span>
                  </div>
                ) : (
                  <div style={{ fontSize: '11px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                    {isLoading ? <span className="spinner" /> : 'Click to expand'}
                  </div>
                )}

                {/* Orders */}
                <div style={{ fontSize: '11px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                  {p.orders} orders
                </div>
              </div>

              {/* ── Expanded panel ───────────────── */}
              {isOpen && (
                <div style={{
                  margin: '0 0 4px 40px',
                  background: 'var(--surface-tint)',
                  border: '1px solid var(--accent-border)',
                  borderTop: `2px solid ${view === 'unsold' ? 'var(--red)' : 'var(--green)'}`,
                  borderRadius: '0 0 10px 10px',
                  padding: '14px 16px',
                  animation: 'slideDown .2s ease',
                }}>

                  {isLoading ? (
                    <div style={{ textAlign: 'center', padding: '16px', color: 'var(--muted)', fontSize: '13px' }}>
                      <span className="spinner" /> Loading…
                    </div>

                  ) : !info ? (
                    <div style={{ color: 'var(--muted)', fontSize: '13px' }}>Failed to load</div>

                  ) : (
                    <>
                      {/* Summary bar */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, height: '10px', background: 'var(--border)', borderRadius: '5px', overflow: 'hidden', minWidth: '120px' }}>
                          <div style={{
                            height: '100%', borderRadius: '5px', background: 'var(--green)',
                            width: `${(info.sold_in / info.total_states) * 100}%`,
                            transition: 'width .5s ease',
                          }} />
                        </div>
                        <span style={{ fontSize: '12px', color: 'var(--green-dark)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {info.sold_in} / {info.total_states} states
                        </span>
                        <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
                          ({((info.sold_in / info.total_states) * 100).toFixed(0)}% coverage)
                        </span>
                      </div>

                      {/* Section title */}
                      <div style={{
                        fontSize: '12px', fontWeight: 600, marginBottom: '10px',
                        color: view === 'unsold' ? 'var(--red-dark)' : 'var(--green-dark)',
                      }}>
                        {view === 'unsold'
                          ? `❌ Not sold in ${info.not_sold_in} states`
                          : `✅ Sold in ${info.sold_in} states`}
                      </div>

                      {/* States grid */}
                      {showList.length === 0 ? (
                        <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                          {view === 'unsold' ? '🎉 Sold in all states!' : 'No states found'}
                        </div>
                      ) : (
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                          gap: '6px',
                        }}>
                          {showList.map(state => (
                            <div key={state} style={{
                              display: 'flex', alignItems: 'center', gap: '6px',
                              padding: '5px 10px', borderRadius: '6px', fontSize: '12px',
                              background: view === 'unsold' ? 'var(--red-light)' : 'var(--green-light)',
                              border: `1px solid ${view === 'unsold' ? 'var(--red-border)' : 'var(--green-border)'}`,
                              color: view === 'unsold' ? 'var(--red-dark)' : 'var(--toast-success-text)',
                            }}>
                              <span style={{ fontSize: '10px' }}>
                                {view === 'unsold' ? '✕' : '✓'}
                              </span>
                              <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {state}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}